import type { RequestOptions } from "../common/server";
import type {
  AddIssueCommentModel,
  AddIssueModel,
  DeleteIssueCommentJs,
  DeleteIssueCommentModel,
  DeleteIssueJs,
  DeleteIssueModel,
  FeedbackPollingInfoJs,
  ImagePreUploadJs,
  ImagePreUploadModel,
  MarkAllCommentsAsReadModel,
  MarkIssueAsUnreadModel,
  ResolveIssueModel,
  UpdateIssueCommentModel,
  UpdateIssueLastViewedTimestampModel,
  UpdateIssueModel,
  UpdatePinColorModel,
  UpdatePinLocationModel,
} from "./issue.types";
import type { FbEnabledJs, GetIssueCommentJs, GetIssueJs, IssueCommentJs, IssueJs, IssueListJs, UserSuggestions } from "@/generated/models";
import { createEventHook } from "@vueuse/core";
import { jsDateFromAspDateString } from "@/common/lib/date";
import { createUniqueTag } from "@/common/utils";
import { objectToFormData } from "@/services/utils/formData";
import { server } from "./server";

export async function markShortcutAsRead(options: { shortcut: string; pageId?: string; upUntil?: Date }) {
  const model: MarkAllCommentsAsReadModel = {
    shortcut: options.shortcut,
    pageId: options?.pageId,
  };

  if (options?.upUntil) {
    model.upUntil = options.upUntil.toISOString();
  }

  const formData = objectToFormData(model);
  await server.post("issue/MarkAllCommentsAsRead", { body: formData });
}

export const issueAdded = createEventHook<string>();
export const commentAdded = createEventHook<string>();

interface GetUserSuggestionsOptions {
  shortcut: string;
}
export async function getUserSuggestions({ shortcut }: GetUserSuggestionsOptions, { signal }: RequestOptions = {}) {
  const response = await server.get("issue/getUserSuggestions", {
    searchParams: {
      shortcut,
    },
    signal,
  });
  return await response.json() as UserSuggestions;
}

interface GetIssuesListOptions {
  shortcut: string;
  pageId?: string;
}
export async function getIssuesList({ shortcut, pageId }: GetIssuesListOptions, { signal }: RequestOptions = {}) {
  const response = await server.get("issue/list", {
    searchParams: {
      shortcut,
      pageId,
    },
    signal,
  });

  return await response.json() as IssueListJs;
}

export interface PollForUpdates {
  shortcut: string;
  lastPollTime: string;
  creator: string;
  userId?: string;
  pageId?: string;
}

export async function pollForUpdates(
  params: PollForUpdates,
  { signal }: RequestOptions = {},
) {
  const {
    shortcut,
    lastPollTime,
    creator,
    userId,
    pageId,
  } = params;

  const response = await server.get("issue/pollForUpdates", {
    searchParams: {
      shortcut,
      pageId,
      lastPollTime,
      creator,
      userId,
    },
    signal,
  });

  return await response.json() as FeedbackPollingInfoJs;
}

export interface AddIssue {
  shortcut: string;
  userId: string;
  userName: string;
  pageId: string;
  pageName: string;
  x?: number;
  y?: number;
  relativeX?: number;
  relativeY?: number;
  color: string;
  content: string;
  images?: string[];
}

export async function addIssue(
  params: AddIssue,
) {
  const {
    shortcut,
    userId,
    userName,
    pageId,
    pageName,
    content,
    x,
    y,
    relativeX,
    relativeY,
    color,
    images,
  } = params;

  const model: AddIssueModel = {
    tag: createUniqueTag(),
    shortcut,
    pageId,
    pageName,
    userId,
    creator: userName,
    description: content,
    isRichText: true,
    x: x ? Math.trunc(x) : undefined,
    y: y ? Math.trunc(y) : undefined,
    relativeX,
    relativeY,
    color,
    pinNumber: 0,
    images: JSON.stringify(images ?? []),
  };

  const formData = objectToFormData(model);
  const response = await server.post("issue/add", { body: formData });
  issueAdded.trigger(shortcut);
  return await response.json() as GetIssueJs;
}

export interface UpdateIssueParams {
  shortcut: string;
  userId: string;
  userName: string;
  issue: IssueJs;
  newContent: string;
  screenshots: {
    ids: string[];
    images: string[];
    thumbnails: string[];
  };
}

export async function updateIssue({
  shortcut,
  userId,
  userName,
  issue,
  newContent,
  screenshots,
}: UpdateIssueParams) {
  const model: UpdateIssueModel = {
    shortcut,
    userId,
    tag: createUniqueTag(),
    pageId: issue.pageId,
    issueId: issue.code,
    creator: userName,
    issueText: newContent,
    isRichText: true,
    images: JSON.stringify(screenshots.ids),
  };

  const formData = objectToFormData(model);
  const response = await server.post("issue/updateIssue", { body: formData });
  return await response.json() as GetIssueJs;
}

export interface AddIssueCommentParams {
  shortcut: string;
  userId: string;
  userName: string;
  issue: IssueJs;
  newContent: string;
  images?: string[];
}

export async function addIssueComment({
  shortcut,
  userId,
  userName,
  issue,
  newContent,
  images,
}: AddIssueCommentParams) {
  const model: AddIssueCommentModel = {
    shortcut,
    userId,
    tag: createUniqueTag(),
    pageId: issue.pageId,
    issueCode: issue.code,
    commentText: newContent,
    commenter: userName,
    isRichText: true,
    images: JSON.stringify(images ?? []),
  };

  const formData = objectToFormData(model);
  const response = await server.post("issue/addComment", { body: formData });
  commentAdded.trigger(shortcut);
  return await response.json() as GetIssueCommentJs;
}

export interface UpdateIssueCommentParams {
  shortcut: string;
  userId: string;
  userName: string;
  issue: IssueJs;
  comment: IssueCommentJs;
  newContent: string;
  screenshots: {
    ids: string[];
    images: string[];
    thumbnails: string[];
  };
}

export async function updateIssueComment({
  shortcut,
  userId,
  userName,
  issue,
  comment,
  newContent,
  screenshots,
}: UpdateIssueCommentParams) {
  const model: UpdateIssueCommentModel = {
    shortcut,
    userId,
    tag: createUniqueTag(),
    pageId: issue.pageId,
    commentId: comment.id,
    commenter: userName,
    commentText: newContent,
    isRichText: true,
    images: JSON.stringify(screenshots.ids),
  };

  const formData = objectToFormData(model);
  const response = await server.post("issue/updateComment", { body: formData });
  return await response.json() as GetIssueCommentJs;
}

export interface DeleteIssueParams {
  shortcut: string;
  userId: string;
  userName: string;
  issue: IssueJs;
}

export async function deleteIssue({
  shortcut,
  userId,
  userName,
  issue,
}: DeleteIssueParams) {
  const model: DeleteIssueModel = {
    shortcut,
    tag: createUniqueTag(),
    commenter: userName,
    userId,
    issueCode: issue.code,
  };

  const formData = objectToFormData(model);
  const response = await server.post("issue/delete", { body: formData });
  return await response.json() as DeleteIssueJs;
}

export interface DeleteIssueCommentParams {
  shortcut: string;
  userId: string;
  userName: string;
  comment: IssueCommentJs;
}

export async function deleteIssueComment({
  shortcut,
  userId,
  userName,
  comment,
}: DeleteIssueCommentParams) {
  const model: DeleteIssueCommentModel = {
    shortcut,
    tag: createUniqueTag(),
    commenter: userName,
    userId,
    commentId: comment.id,
  };

  const formData = objectToFormData(model);
  const response = await server.post("issue/deleteComment", { body: formData });
  return await response.json() as DeleteIssueCommentJs;
}

export interface ResolveIssueParams {
  userId: string;
  userName: string;
  issue: IssueJs;
  isResolve: boolean;
}

export async function resolveIssue({
  userId,
  userName,
  issue,
  isResolve,
}: ResolveIssueParams) {
  const model: ResolveIssueModel = {
    userId,
    isResolve,
    pageId: issue.pageId,
    creator: userName,
    issueCode: issue.code,
  };

  const formData = objectToFormData(model);
  const response = await server.post("issue/resolveIssue", { body: formData });
  return await response.json() as GetIssueJs;
}

export interface SetPinLocationParams {
  issue: IssueJs;
  x: number;
  y: number;
  relativeX?: number;
  relativeY?: number;
}

export function setPinLocation({
  issue,
  x,
  y,
  relativeX,
  relativeY,
}: SetPinLocationParams) {
  const model: UpdatePinLocationModel = {
    issueCode: issue.code,
    x: Math.round(x),
    y: Math.round(y),
    relativeX,
    relativeY,
  };

  const formData = objectToFormData(model);
  return server.post("issue/updatePinLocation", { body: formData });
}

export interface SetPinColorParams {
  issue: IssueJs;
  color: string;
}

export function setPinColor({ issue, color }: SetPinColorParams) {
  const model: UpdatePinColorModel = {
    issueCode: issue.code,
    color,
  };

  const formData = objectToFormData(model);
  return server.post("issue/updatePinColor", { body: formData });
}

export interface UpdateIssueLastViewedParams {
  shortcut: string;
  issue: IssueJs;
}

export async function updateIssueLastViewed(
  { shortcut, issue }: UpdateIssueLastViewedParams,
) {
  const model: UpdateIssueLastViewedTimestampModel = {
    shortcut,
    issueId: issue.id,
    lastViewedCommentTimestamp: jsDateFromAspDateString(issue.changedDate).toISOString(),
  };

  const formData = objectToFormData(model);
  return server.post("issue/updateLastViewedCommentTimestamp", { body: formData });
}

export interface MarkIssueAsUnreadParams {
  shortcut: string;
  issue: IssueJs;
}
export async function markIssueAsUnread({ shortcut, issue }: MarkIssueAsUnreadParams) {
  const model: MarkIssueAsUnreadModel = {
    shortcut,
    issueId: issue.id,
  };
  const formData = objectToFormData(model);
  return server.post("issue/markIssueAsUnread", { body: formData });
}

export async function imagePreUpload(
  model: ImagePreUploadModel,
) {
  const formData = objectToFormData(model);
  const response = await server.post("issue/ImagePreUpload", { body: formData });
  return await response.json() as ImagePreUploadJs;
}

export async function getFeedbackEnabled(shortcut: string, { signal }: RequestOptions = {}) {
  const response = await server.get("issue/fbenabled", {
    searchParams: { shortcut },
    signal,
  });
  return await response.json() as FbEnabledJs;
}
